import type { UndefinedOnPartialDeep } from 'type-fest';

import type { DtoCustomerContact } from '@repo/api-types/order-retrieval';
import type {
    AssetDataType,
    CustomerType,
    PartialDraftAssetDetails,
    PartialQuoteAssetDetails,
} from '@repo/types/asset';
import { type CustomerData, isDtoCustomerContact } from '@repo/types/customer';

import { getAssetData } from './customer-assets.selectors';
import { getCustomerViewType } from './utils.ts';

const selectDeliveryContactFn = getAssetData(
    (data) => data.assetDetails._embedded?.deliveryContact,
    (data) => data.assetDetails.cartData?.customer,
    (data) => data.assetDetails.cartData?.customer
);

const selectBillingContactFn = getAssetData(
    (data) => data.assetDetails._embedded?.billingContact,
    (data) => data.assetDetails.cartData?.customer,
    (data) => data.assetDetails.cartData?.customer
);

export const selectEmployeeNo = getAssetData(
    (data) => data.assetDetails._embedded?.deliveryContact?.customerContactDetails?.employeeNo,
    (): undefined => undefined,
    (): undefined => undefined
);

export const selectDeliveryContact = (data?: AssetDataType): UndefinedOnPartialDeep<CustomerData> | undefined => {
    const deliveryContact = selectDeliveryContactFn(data);

    if (!deliveryContact) {
        return undefined;
    }
    return normalizeData(deliveryContact);
};

export const selectBillingContact = (data?: AssetDataType): UndefinedOnPartialDeep<CustomerData> | undefined => {
    const billingContact = selectBillingContactFn(data);

    if (!billingContact) {
        return undefined;
    }

    return normalizeData(billingContact);
};

type Customer = DtoCustomerContact | CustomerData;

const normalizeData = (customer: Customer): UndefinedOnPartialDeep<CustomerData> => {
    if (isDtoCustomerContact(customer)) {
        return {
            addressLine1: customer.address?.addressLine1,
            addressLine2: customer.address?.addressLine2,
            addressLine3: customer.address?.addressLine3,
            businessName: customer.customerContactDetails?.businessName,
            city: customer.address?.cityName,
            countryCode: customer.address?.countryCode,
            email: customer.customerContactDetails?.contactTypeValues?.EMAIL,
            firstName: customer.customerContactDetails?.firstName,
            id: customer.customerContactDetails?.globalCustomerId,
            ikeaFamilyNumber: customer.customerContactDetails?.familyNumber,
            isBusinessCustomer: Boolean(customer.customerContactDetails?.businessName),
            landlinePhone: customer.customerContactDetails?.contactTypeValues?.PHONE,
            lastName: customer.customerContactDetails?.lastName,
            mobilePhone: customer.customerContactDetails?.contactTypeValues?.MOBILE,
            partyId: customer.customerContactDetails?.globalCustomerId,
            preferredLocale: customer.customerContactDetails?.locale,
            stateCode: customer.address?.stateCode,
            vatPayer: 'NA',
            zipCode: customer.address?.zipCode,
        };
    }

    return customer;
};

export const selectCustomerIdentifier = getAssetData(
    (data) =>
        data.assetDetails._embedded?.billingContact?.customerContactDetails?.globalCustomerId ??
        data.assetDetails._embedded?.paymentPlans?.[0]?.creditPayments?.[0]?.paymentTransactions?.[0]?.maskedCardNumber,
    (data) => data.assetDetails.customerInfo?.id,
    (data) => data.assetDetails.customerIdentifier?.id ?? data.assetDetails.customerInfo?.id
);

export const extractCustomerViewType = (
    assetDetails: PartialDraftAssetDetails | PartialQuoteAssetDetails
): CustomerType => {
    const customer = assetDetails.cartData?.customer;

    return getCustomerViewType(customer);
};
